import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../firebase-config';

// NOTE: Using branded email callable function instead of Firebase's default (ADR-077)

const EmailVerificationBanner = () => {
  const { currentUser } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  // Only show banner if user is logged in and email is not verified
  if (!currentUser || currentUser.emailVerified) {
    return null;
  }

  const handleResendVerification = async () => {
    setSending(true);
    setError(null);

    try {
      // Force fresh token before calling Cloud Function
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
      }

      const sendVerificationEmailFn = httpsCallable(functions, 'sendVerificationEmail');
      await sendVerificationEmailFn({});
      setSent(true);
      // Reset sent state after 5 seconds
      setTimeout(() => setSent(false), 5000);
    } catch (error) {
      console.error('Error sending verification email:', error);
      if (error.code === 'auth/too-many-requests') {
        setError('Too many requests. Please try again later.');
      } else {
        setError('Failed to send verification email. Please try again.');
      }
    }

    setSending(false);
  };

  return (
    <>
      <style>{`
        .email-verification-banner {
          background: #fef7e0;
          border-bottom: 1px solid #f9ab00;
          padding: 12px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          animation: slideDown 0.3s ease-out;
        }

        @keyframes slideDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .banner-content {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
        }

        .banner-icon {
          color: #f9ab00;
          font-size: 24px;
        }

        .banner-text {
          font-size: 14px;
          color: #5f6368;
        }

        .banner-text strong {
          color: #202124;
          font-weight: 500;
        }

        .banner-actions {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .verify-button {
          padding: 6px 16px;
          background: #f9ab00;
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .verify-button:hover {
          background: #ea8600;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .verify-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .success-message {
          color: #1e8e3e;
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .error-message {
          color: #d93025;
          font-size: 14px;
        }

        .close-button {
          background: none;
          border: none;
          color: #5f6368;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .close-button:hover {
          color: #202124;
        }

        @media (max-width: 768px) {
          .email-verification-banner {
            flex-direction: column;
            align-items: flex-start;
            padding: 12px 16px;
          }

          .banner-content {
            width: 100%;
          }

          .banner-actions {
            width: 100%;
            justify-content: flex-start;
          }
        }
      `}</style>

      <div className="email-verification-banner">
        <div className="banner-content">
          <span className="material-icons banner-icon">warning</span>
          <div className="banner-text">
            <strong>Verify your email address</strong> - We sent a verification link to {currentUser.email}
          </div>
        </div>
        
        <div className="banner-actions">
          {sent ? (
            <div className="success-message">
              <span className="material-icons" style={{ fontSize: '18px' }}>check_circle</span>
              Verification email sent!
            </div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : (
            <button 
              className="verify-button" 
              onClick={handleResendVerification}
              disabled={sending}
            >
              {sending ? (
                <>
                  <span className="material-icons" style={{ fontSize: '18px', animation: 'spin 1s linear infinite' }}>refresh</span>
                  Sending...
                </>
              ) : (
                <>
                  <span className="material-icons" style={{ fontSize: '18px' }}>email</span>
                  Resend Email
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default EmailVerificationBanner;